import type { SharedFolderCreateMessageDatagramBuf } from "~/classes/generated/message"
import type { SharedFolderCreateResponseDatagramBuf } from "~/classes/generated/response"

import { DatagramBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SharedFolderCreateRequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { addFileIdToBuf, addSharedFolderIdToBuf, parseSharedFolderId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("sharedFolder/create")

export function sendSharedFolderCreateRequest(request: SharedFolderCreateRequestDatagram) {
  const { sendWorkerDatagramBuf } = useCache()

  const fbb = new DatagramBufferBuilder(64)
  const home = addFileToBuf(fbb, request.home)
  const archive = addFileToBuf(fbb, request.archive)
  const requestId = addRequestIdToBuf(fbb, request.requestId)
  const sharedFolderPreHash = addSharedFolderIdToBuf(fbb, request.sharedFolderPreHash)
  const homePreHash = addFileIdToBuf(fbb, request.homePreHash)
  const archivePreHash = addFileIdToBuf(fbb, request.archivePreHash)

  SharedFolderCreateRequestDatagramBuf.startSharedFolderCreateRequestDatagramBuf(fbb)
  SharedFolderCreateRequestDatagramBuf.addRequestId(fbb, requestId)
  SharedFolderCreateRequestDatagramBuf.addSharedFolderPreHash(fbb, sharedFolderPreHash)
  SharedFolderCreateRequestDatagramBuf.addHomePreHash(fbb, homePreHash)
  SharedFolderCreateRequestDatagramBuf.addArchivePreHash(fbb, archivePreHash)
  SharedFolderCreateRequestDatagramBuf.addHome(fbb, home)
  SharedFolderCreateRequestDatagramBuf.addArchive(fbb, archive)
  SharedFolderCreateRequestDatagramBuf.addOffline(fbb, request.offline)
  const createRequest = SharedFolderCreateRequestDatagramBuf.endSharedFolderCreateRequestDatagramBuf(fbb)

  RequestDatagramBuf.startRequestDatagramBuf(fbb)
  RequestDatagramBuf.addSharedFolderCreateRequest(fbb, createRequest)
  const requests = DatagramBuf.createRequestVector(fbb, [RequestDatagramBuf.endRequestDatagramBuf(fbb)])

  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, requests)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))

  sendWorkerDatagramBuf(fbb.asUint8Array())
}

export function handleSharedFolderCreateResponseDatagramBuf(datagram: SharedFolderCreateResponseDatagramBuf) {
  onSharedFolderCreateResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    sharedFolderId: parseSharedFolderId(datagram.sharedFolderId()!),
    home: maybe(parseFile)(datagram.home()),
    archive: maybe(parseFile)(datagram.archive()),
    offline: datagram.offline(),
  })
}

export function onSharedFolderCreateResponse(response: SharedFolderCreateResponseDatagram) {
  const { emitInterfaceEvent, emitEntityEvent } = useEvents()

  emitInterfaceEvent("sharedFolderCreateResponse", response, response.requestId)

  if (response.error)
    return onSharedFolderCreateErrored(response.error)
  if (!response.home || !response.archive)
    throw new Error("No space info in response")

  const { sharedFolderId, home, archive } = response

  emitEntityEvent("fileCreated", response.home.id, response.home, response.offline)
  emitEntityEvent("fileCreated", response.archive.id, response.archive, response.offline)
  emitEntityEvent("sharedFolderCreated", response.sharedFolderId, {
    sharedFolderId,
    home,
    archive,
  }, response.offline)
}

export function onSharedFolderCreateErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()
  return notifyDatagramError(error, "Folder creation was unsuccessful")
}

export function handleSharedFolderCreateMessageDatagramBuf(
  _datagram: SharedFolderCreateMessageDatagramBuf,
) {
  throw new Error("No handler for create messages yet")
}

export function onSharedFolderCreateMessage(message: SharedFolderCreateMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  console.debug("Space creation message", message)

  emitEntityEvent("fileCreated", message.home.id, message.home, false)
  emitEntityEvent("fileCreated", message.archive.id, message.archive, false)
}
